import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/useAuth';
import { useAccountContext } from '@/auth/useAccountContext';
import { resolveLocale } from '@/i18n';
import type { AuthMode } from '@/lib/api';

import { createApiClient, type ApiClient } from './client';

const ApiClientContext = createContext<ApiClient | null>(null);

export function ApiClientProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { activeAccountId } = useAccountContext();
  const { i18n } = useTranslation();

  const mode: AuthMode = auth.status === 'authenticated' ? auth.mode : 'standalone';
  const locale = resolveLocale(i18n.language);

  const client = useMemo(
    () => createApiClient(mode, activeAccountId, locale),
    [mode, activeAccountId, locale],
  );

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error('useApiClient must be used within <ApiClientProvider>');
  return ctx;
}
